import type { ReactNode } from 'react';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * THE AUTH CARD
 *
 * One panel shape for every page somebody meets before they are signed in —
 * login, forgotten password, reset, first-password-change. They are the same
 * moment in the product wearing four different layouts otherwise, and the one
 * a person sees is decided by whichever link they happened to follow.
 *
 * Text on it is black and grey, with no accent anywhere. An early cut set the
 * subtitle in the warm `clay` tone the first template used, which on a sign-in
 * card reads as a warning — a coloured line under the heading sits exactly
 * where errors appear. Red is kept for the two places something has actually
 * gone wrong: a field message and the alert banner.
 * ═══════════════════════════════════════════════════════════════════════════
 */

/**
 * The full-screen ground the card floats on.
 *
 * Used by the standalone auth pages. The login page does not use it — it has
 * the two-column brand layout, and its right column supplies the same tint
 * itself.
 */
export function AuthShell({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-brand-50/40 px-5 py-10">
      <div className="mb-6 flex items-center gap-[0.25cm]">
        <span className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-[10px] bg-card shadow-sm ring-1 ring-black/[0.06]">
          <img src="/petpooja-mark.png" alt="" className="h-[21px] w-[21px] object-contain" />
        </span>
        <div className="leading-[1.25]">
          <p className="text-[13.5px] font-semibold tracking-[-0.01em]">Petpooja</p>
          <p className="text-[11px] text-muted-foreground">Learning Management System</p>
        </div>
      </div>

      <div className="w-full max-w-[400px]">{children}</div>
    </div>
  );
}

export function AuthCard({
  title,
  subtitle,
  children,
  className,
}: {
  title: string;
  /** The one grey line under the heading. */
  subtitle?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        // A generous radius and a soft, low shadow: the card should read as
        // lifted off the ground, not outlined against it.
        'w-full rounded-[22px] bg-card p-7 ring-1 ring-black/[0.05] sm:p-9',
        'shadow-[0_1px_2px_rgba(16,24,40,0.04),0_16px_40px_-16px_rgba(16,24,40,0.18)]',
        className,
      )}
    >
      {/*
        Black, not an accent.

        The subtitle was in the warm accent, matching the template it came
        from. On a sign-in card a coloured line directly under the heading
        reads as a warning — the place errors appear — so it announced a
        problem on a page where nothing had gone wrong yet. The reference sets
        "to access Account" in plain grey, and it is right to.
      */}
      <h1 className="text-[21px] font-bold leading-tight tracking-[-0.02em]">{title}</h1>
      {subtitle && (
        <p className="mt-1 text-[13px] leading-[1.5] text-muted-foreground">{subtitle}</p>
      )}

      <div className="mt-6">{children}</div>
    </div>
  );
}

/**
 * A field label.
 *
 * No red asterisk. Every field on every one of these forms is required, so it
 * marked nothing — it just put the error colour beside each label before
 * anybody had made a mistake. The requirement is carried by the input's own
 * `aria-required`, which is where it belongs and where a screen reader looks;
 * an asterisk announced as "star" tells nobody anything.
 */
export function FieldLabel({
  htmlFor,
  children,
  className,
}: {
  htmlFor: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <Label htmlFor={htmlFor} className={cn('text-[13px] font-medium', className)}>
      {children}
    </Label>
  );
}

/** A labelled rule — "or" in the reference — separating SSO from the email form. */
export function AuthDivider({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-3">
      <span className="h-px flex-1 bg-border" aria-hidden />
      <span className="text-[12px] text-muted-foreground">{label}</span>
      <span className="h-px flex-1 bg-border" aria-hidden />
    </div>
  );
}
